import { useCallback, useEffect, useState, useRef } from 'react';

import { currencyToSymbol } from '@simplywallst/formatting/dist/support';

import { InformationSolid } from '@simplywallst/ui-core/icons';
import { Grid, Text, Textfield } from '@simplywallst/ui-core';

import { formatterCurrency } from '@/utilities/format';

import Tooltip from '@/components/Tooltip';
import { CompanyIntrinsicDiscount } from '@/components/CompanyIntrinsicDiscount';

import type { WatchlistItem } from '@/hooks/useWatchlist';

import { FairValueTooltip } from './Tooltip';
import { CurrencyPrefix } from './styled';

interface Props {
  disabled: boolean;
  userFairValue: number | null;
  company: WatchlistItem['company'];
  onUpdate: (value: number) => void;
}

export const FairValueInput = ({
  disabled,
  userFairValue,
  company,
  onUpdate,
}: Props) => {
  const input = useRef<HTMLInputElement>(null);
  const [fairValue, setFairValue] = useState<string>('');

  const { lastSharePrice: companySharePrice } = company.analysisValue;
  const { trading_item_currency_iso: companyCurrencyISO } =
    company.data.currencyInfo;
  const companyCurrencySymbol: string | undefined =
    currencyToSymbol[companyCurrencyISO]?.currency.symbol;

  useEffect(() => {
    const { current } = input;
    if (current) {
      setTimeout(() => current.focus(), 1);
    }
  }, []);

  useEffect(() => {
    if (userFairValue === null) {
      setFairValue('');
      return;
    }
    const value = String(userFairValue);
    /**
     * Floats and integers only
     *
     * ".0"          => false
     * "0.000"       => true
     * "123"         => true
     * "123.001"     => true
     * "...0abc."    => false
     * "abc0.]][]1"  => false
     * "    0.   1"  => false
     */
    if (/^([0-9]+([.][0-9]*)?|[.][0-9]+)$/g.test(value)) {
      setFairValue(value);
    }
  }, [userFairValue]);

  const handleChange = useCallback(() => {
    if (input.current !== null) {
      const value = input.current.value;
      if (value.length === 0) {
        setFairValue('');
      } else if (/^([0-9]+([.][0-9]*)?|[.][0-9]+)$/g.test(value)) {
        setFairValue(value);
      }
    }
  }, [input]);

  const handleUpdate = useCallback(() => {
    if (input.current !== null) {
      onUpdate(Number.parseFloat(input.current.value));
    }
  }, [input, onUpdate]);

  return (
    <Grid
      templateColumns="50% 33%"
      templateColumnsLg="67% 25%"
      justifyContent="space-between"
    >
      <label>
        <Grid
          templateColumns="max-content min-content"
          alignItems="center"
          gap="4px"
        >
          <Text typography="xSmallStrong" color="spaceGrayRGBA" opacity={0.7}>
            Your fair value
          </Text>
          <Tooltip zIndex={9999} placement="top" title={<FairValueTooltip />}>
            <InformationSolid opacity={0.5} />
          </Tooltip>
        </Grid>
        <Textfield
          disabled={disabled}
          ref={input}
          prefix={
            typeof companyCurrencySymbol === 'undefined' ? undefined : (
              <CurrencyPrefix>{companyCurrencySymbol}</CurrencyPrefix>
            )
          }
          styleType="standardReverse"
          name="fair-value-input"
          type="text"
          placeholder="0.00"
          value={fairValue}
          onBlur={handleUpdate}
          onChange={handleChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleUpdate();
            }
          }}
        />
      </label>
      <div>
        <Grid templateRows="auto auto" gap="4px">
          <Text typography="xSmallStrong" color="spaceGrayRGBA" opacity={0.7}>
            Current price
          </Text>
          <Text typography="xSmall" color="spaceGray">
            {formatterCurrency(companySharePrice, companyCurrencyISO)}
          </Text>
        </Grid>
        <Text typography="tinyCondensed" color="spaceGray">
          <CompanyIntrinsicDiscount
            company={company}
            fairValue={Number.parseFloat(fairValue)}
          />
        </Text>
      </div>
    </Grid>
  );
};
